/**
 * Real arms, where a title genuinely bore some.
 *
 * The painted blazons in `blazon.ts` cover every title in the game, because
 * every title needs *something* on its shield. This covers the much smaller
 * set for which a historical coat exists and has been checked against 1212:
 * those get the real thing, drawn from Wikimedia Commons, and everything else
 * keeps the painted version.
 *
 * The gap is not a backlog. Most of the duchies are generated names, ten of
 * the twelve empires are inventions of this game, and several realms that
 * certainly existed - the Almohads, the Ayyubids, Rum, Baghdad - had no
 * European heraldry at all. A painted device for a place that never had one is
 * the honest answer; a downloaded shield would just be a costume.
 *
 * Keys mirror how a title is identified elsewhere: crowns and empires by their
 * string id, duchies by their full name, since that is what the duchy tables
 * carry instead of ids.
 */
import { REAL_ARMS, type RealArms } from '@/assets/art/art';

export type { RealArms };

export function realArmsFor(
  tier: 'county' | 'duchy' | 'kingdom' | 'empire',
  name: string,
  kingdomId: string | null,
  empireId: string | null,
): RealArms | null {
  if (tier === 'empire') return (empireId && REAL_ARMS[`empire:${empireId}`]) || null;
  if (tier === 'kingdom') return (kingdomId && REAL_ARMS[`kingdom:${kingdomId}`]) || null;
  if (tier === 'duchy') return REAL_ARMS[`duchy:${name}`] ?? null;
  // Counties have no arms of their own; the county frame is solid for that reason.
  return null;
}

/** Every credited source, for the attributions screen. Public domain excluded. */
export function creditedArms(): RealArms[] {
  return Object.values(REAL_ARMS)
    .filter((a) => !/public domain|^cc0/i.test(a.licence))
    .sort((a, b) => a.source.localeCompare(b.source));
}
